<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems } from '~/data/pages/microcredit/mocks'
import {
  WidgetBankPageRatesChart,
  WidgetBankPageRatesConverter,
  WidgetBankPageRatesWrapper
} from '~/components/widget/bank-page'
import type { IResponseData } from '~/types'
import { WidgetSidebarRates } from '~/components/widget/sidebar-rates'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetRateAlertSidebar } from '~/components/widget/rate-alert-sidebar'
import type { IOrganizationRateData } from '~/components/feature/rate-alert'
import type { IMicroCredit } from '~/components/entity/microcredit/item'
import { WidgetAboutPageHeader } from '~/components/widget/about-page'

const route = useRoute()
const { data } = await useApiWith404<IResponseData<IMicroCredit>>(
  () => `/microcredit/${route.params.microcredit}`,
  { key: `microcredit-rates-${route.params.microcredit}` }
)
const microcredit = computed(() => data.value?.data)

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: microcredit.value?.title
  }
}))

const { data: ratesData, status: ratesStatus } = await useFetch(
  '/api/rates/bank-cash-cashless-cached',
  {
    params: { bankId: microcredit.value?.id, organization_type: 'microcredit_company' },
    // The organization request may have failed or been cancelled by a quick navigation
    immediate: !!microcredit.value?.id
  }
)

const hasRates = computed(() => {
  if (ratesStatus.value === 'pending') return true
  const regular = ratesData.value?.currentRegularRates ?? {}
  const cashless = ratesData.value?.currentCashlessRates ?? {}
  return Object.keys(regular).length > 0 || Object.keys(cashless).length > 0
})

const { t, locale } = useI18n()

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.microcredits'), url: '/microcredit' },
  {
    title: microcredit.value?.title || t('common.microcredits'),
    url: `/microcredit/${route.params.microcredit}`
  },
  {
    title: t('common.exchange_rates'),
    url: `/microcredit/${route.params.microcredit}/rates`
  }
])

const fitOrganization = computed(() => ({
  type: microcredit.value?.type || '',
  title: microcredit.value?.title || '',
  logo: microcredit.value?.logo,
  organization: {
    official_title: microcredit.value?.official_title || ''
  },
  views_count: microcredit.value?.views_count || 0,
  updated_at: microcredit.value?.updated_at || ''
}))

const { data: organizationsRatesData } = await useFetch('/api/rates/organizations-cached', {
  query: {
    lang: locale.value || 'ru'
  }
})

const allOrganizationsRates = computed<IOrganizationRateData[]>(() => {
  return (
    organizationsRatesData.value?.data.map((item) => ({
      id: item.id,
      title: item.title,
      rates: item.rates
    })) || []
  )
})

const ratesUpdatedAt = computed<string | undefined>(() => {
  const orgs = organizationsRatesData.value?.data
  if (!orgs || !microcredit.value?.id) return undefined
  return orgs.find((item: { id: number; created_at: string }) => item.id === microcredit.value!.id)
    ?.created_at
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="microcredit"
              :organization="microcredit"
              :type="'microcredit'"
            />
            <WidgetRateAlertSidebar
              v-if="microcredit"
              :bank-id="microcredit.id"
              :bank-name="microcredit.title"
              :all-organizations-rates="allOrganizationsRates"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <SidebarBlock variant="white" :heading-text="$t('common.exchange_rates')">
              <WidgetSidebarRates />
            </SidebarBlock>
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetAboutPageHeader
            :type="'microcredit'"
            :product="fitOrganization"
            :heading="t('common.exchange_rates')"
          >
            <WidgetBankServicesMenu
              v-if="microcredit && microcredit.status === 'active'"
              :organization="microcredit"
              :nav-items="navItems"
              :type="'microcredit'"
            />
          </WidgetAboutPageHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: microcredit?.official_title ?? ''
            }"
          />
          <ContentBlock v-if="microcredit && hasRates" variant="white">
            <WidgetBankPageRatesWrapper
              :bank-id="microcredit.id"
              :bank-name="microcredit.official_title"
              :bank-url="microcredit.url"
              :rates-data="ratesData"
              :rates-status="ratesStatus"
              :updated-at="ratesUpdatedAt"
            />
          </ContentBlock>
          <ContentBlock
            v-if="microcredit && hasRates"
            :heading-text="$t('common.currency_converter')"
            variant="white"
          >
            <WidgetBankPageRatesConverter
              :bank-id="microcredit.id"
              :bank-name="microcredit.official_title"
              :rates-data="ratesData"
              :rates-status="ratesStatus"
            />
          </ContentBlock>
          <ContentBlock v-if="microcredit" variant="white">
            <WidgetBankPageRatesChart
              :id="microcredit.id"
              :title="microcredit.title"
              organization-type="microcredit_company"
            />
          </ContentBlock>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
